import './styles.scss';

import { useMutation } from '@apollo/client';
import { Button, Link } from '@ets-global/b2c-website-ui';
import classNames from 'classnames';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SCORING_DOCUMENT_TYPE } from '../../../../constants';
import { SEND_DIGITAL_DOCUMENT } from '../../../../graphql/mutations';
import { extractFilenameFromUrl } from '../../../../helpers/Formatter/StringFormatter';
import useDataLayer from '../../../../hooks/useDataLayer';
import useNotification from '../../../../hooks/useNotification';
import useRouter from '../../../../hooks/useRouter';
import { paths } from '../../../../paths';
import CandidatePictureUpload from '../../../CandidatePictureUpload';
import SessionCandidateIdentity from './Elements/CandidateIdentity';
import SessionCandidateScore from './Elements/Score';
import { ScoreStatusSubReasons } from './Elements/ScoreStatusSubReasons';
import SessionCandidateTooltip from './Elements/Tooltip';
import SendDigitalDocumentByEmail from './SendDigitalDocumentByEmail';

const SessionCandidate = ({
  candidate,
  sessionId,
  hasPhoto,
  past,
  onCancelAppointment,
  onResendConfirmationEmail,
  sessionIsEproctored,
}) => {
  const { generateUrl } = useRouter();
  const { newNotification } = useNotification();
  const { t } = useTranslation();
  const { pushEventPdfDownloaded, pushEventViewMyDigitalScoreReport } = useDataLayer();
  const [sendDigitalDocument] = useMutation(SEND_DIGITAL_DOCUMENT);
  const isMissing = ['status_appointment_no_show', 'status_appointment_refused'].includes(candidate.get('status'));
  const hasScores =
    candidate.get('scoring') &&
    candidate.get('scoring').get('scores') &&
    candidate.get('scoring').get('scores').size > 0;
  const hasScoreStatusSubReason =
    candidate.get('scoring') &&
    candidate.get('scoring').get('scoreStatusSubReasons') &&
    candidate.get('scoring').get('scoreStatusSubReasons').size > 0;
  const digitalDocumentKey =
    hasScores && candidate.get('digitalDocumentKey') ? candidate.get('digitalDocumentKey') : undefined;
  const digitalDocumentURL =
    digitalDocumentKey && !candidate.get('documentUrl')
      ? generateUrl(paths.DIGITAL_SCORE_REPORT, { digitalDocumentKey })
      : candidate.get('documentUrl');

  const onSubmitDigitalDocumentByEmail = ({ email }) => {
    sendDigitalDocument({
      variables: {
        email,
        appointmentId: candidate.get('appointmentId'),
      },
    })
      .then(() => {
        newNotification({
          content: t('account.session.candidate.email-digital-document.sent'),
          type: 'success',
        });
      })
      .catch(() => {
        newNotification({
          content: t('account.session.candidate.email-digital-document.not-sent'),
          type: 'error',
        });
      });
  };

  return (
    <div
      className={classNames('account-session-candidate', {
        'account-session-candidate--with-score': !isMissing && hasScores,
        'account-session-candidate--inline': !isMissing && !hasScores,
      })}
    >
      <div className="account-session-candidate__flex">
        {hasPhoto && (
          <CandidatePictureUpload
            sessionId={sessionId}
            displayOnly={past}
            candidate={candidate.toJS()}
            withInstructions={false}
          />
        )}
        <div
          className={classNames('account-session-candidate__left', {
            'account-session-candidate__left--full': isMissing,
          })}
        >
          <SessionCandidateIdentity candidate={candidate} isMissing={isMissing} />
          {candidate.get('admissionForm') && !sessionIsEproctored && (
            <Link
              aria-label={t(
                past
                  ? 'account.session.candidate.download-button'
                  : 'account.session.candidate.download-incoming-button'
              )}
              icon="download"
              to={candidate.get('admissionForm')}
              className={'button--transparent-light'}
              target={'_blank'}
              style={{ margin: '12px 0 0 3px' }}
              onClick={() =>
                pushEventPdfDownloaded(
                  past ? 'candidate signed admission form' : 'candidate admission form',
                  extractFilenameFromUrl(candidate.get('admissionForm'))
                )
              }
            >
              <Trans>
                {past
                  ? 'account.session.candidate.download-button'
                  : 'account.session.candidate.download-incoming-button'}
              </Trans>
            </Link>
          )}
        </div>
        {!isMissing && (
          <div className={'account-session-candidate__right'}>
            {!past && (
              <SessionCandidateTooltip
                appointmentId={candidate.get('appointmentId')}
                sessionId={sessionId}
                isCancellableByClient={candidate.get('isCancellableByClient')}
                hasAdmissionForm={!!candidate.get('admissionForm')}
                onCancelAppointment={onCancelAppointment}
                onResendConfirmationEmail={onResendConfirmationEmail}
              />
            )}
            {hasScores && <SessionCandidateScore scoring={candidate.get('scoring')} />}
            {hasScoreStatusSubReason && (
              <ScoreStatusSubReasons scoreStatusSubReasons={candidate.get('scoring').get('scoreStatusSubReasons')} />
            )}
            {digitalDocumentURL && !hasScoreStatusSubReason && (
              <Button
                className={'account-session-candidate__button-digital-document'}
                to={digitalDocumentURL}
                target="_blank"
                aria-label={`view my digital score report button`}
                onClick={pushEventViewMyDigitalScoreReport}
              >
                {candidate.get('documentType') === SCORING_DOCUMENT_TYPE.DIGITAL_SCORE_REPORT && (
                  <Trans>digital-score-reports.link.label</Trans>
                )}
                {candidate.get('documentType') === SCORING_DOCUMENT_TYPE.PROOF_OF_COMPLETION_SESSION && (
                  <Trans>proof-of-completion.link.label</Trans>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      {digitalDocumentURL &&
        !hasScoreStatusSubReason &&
        candidate.get('documentType') === SCORING_DOCUMENT_TYPE.DIGITAL_SCORE_REPORT && (
          <SendDigitalDocumentByEmail
            onSubmitDigitalDocumentByEmail={onSubmitDigitalDocumentByEmail}
            documentType={candidate.get('documentType')}
          />
        )}
    </div>
  );
};

SessionCandidate.propTypes = {
  candidate: PropTypes.instanceOf(Map).isRequired,
  sessionId: PropTypes.number.isRequired,
  hasPhoto: PropTypes.bool,
  past: PropTypes.bool,
  reFetchCandidates: PropTypes.func,
};

export default SessionCandidate;
